import { FC, ReactElement, useCallback, useState } from 'react';
import { Box, Typography, IconButton, Tooltip, CircularProgress } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useTranslation } from 'react-i18next';
import { FileIcon } from './FileIcon';
import { Attachment } from '../../services/attachmentService';

interface FileListItemProps {
    attachment: Attachment;
    allowModifications: boolean;
    canDelete: boolean;
    onDelete: (attachment: Attachment) => void;
}

export const FileListItem: FC<FileListItemProps> = ({
    attachment,
    allowModifications,
    canDelete,
    onDelete,
}): ReactElement => {
    const { t } = useTranslation();
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = useCallback(async () => {
        setIsDownloading(true);
        try {
            const response = await fetch(attachment.url);
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = attachment.displayName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error('Download failed:', err);
        } finally {
            setIsDownloading(false);
        }
    }, [attachment.url, attachment.displayName]);

    const iconButtonSx = {
        color: '#98A2B3',
        '&:hover': { color: '#1976d2', backgroundColor: '#EFF6FF' },
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                px: 2,
                py: 1.5,
                '&:hover': {
                    backgroundColor: '#F9FAFB',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
                <FileIcon fileName={attachment.displayName} />
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            color: '#6B4C9A',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {attachment.displayName}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {/* View: open in browser tab without forcing download */}
                <Tooltip title={t('attachment_pane.view')}>
                    <IconButton
                        size="small"
                        component="a"
                        href={attachment.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={iconButtonSx}
                    >
                        <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
                {/* Download: force-download via fetch → object URL (works cross-origin) */}
                {!attachment.external && (
                    <Tooltip title={t('attachment_pane.download')}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleDownload}
                                disabled={isDownloading}
                                sx={iconButtonSx}
                            >
                                {isDownloading
                                    ? <CircularProgress size={14} />
                                    : <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                                }
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                {allowModifications && canDelete && (
                    <Tooltip title={t('attachment_pane.delete')}>
                        <IconButton
                            size="small"
                            onClick={() => onDelete(attachment)}
                            sx={{
                                color: '#98A2B3',
                                '&:hover': { color: '#EF4444', backgroundColor: '#FEF2F2' },
                            }}
                        >
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};
